import { useSEO, buildBreadcrumbsJsonLd, buildServiceJsonLd } from "../../lib/seo";
import { pageFaqJsonLd, useFaqs } from "../../lib/faqs";
import { kbCategoryFor } from "../../lib/kennisbank";
import { KbRelated } from "../kennisbank/KbRelated";
import { ProductHero } from "./ProductHero";
import { ProductSubnav } from "./ProductSubnav";
import { ProductIntro } from "./ProductIntro";
import { ProductOptions } from "./ProductOptions";
import { ServiceScope } from "./ServiceScope";
import { ProductArticle } from "./ProductArticle";
import { ProductPrice } from "./ProductPrice";
import { ProductFAQ } from "./ProductFAQ";
import { IsdeTeaser } from "./IsdeTeaser";
import { ProductEndCTA, RelatedProducts } from "./RelatedProducts";
import { ChoiceGuide, CompareTable, MaterialChoice } from "./OverviewSections";
import { SITE_URL } from "../../lib/constants";
import { usePageSeo } from "../../lib/siteTexts";
import { productTextKey } from "../../data/pageTexts";
import { MEDIA_BY_KEY } from "../../data/siteMedia";
import { DoorGallery } from "../catalog/DoorGallery";
import { ProfileSystemsSection } from "../catalog/ProfileSystemsSection";
import { useDoorCatalog, useProfileSystems } from "../../lib/catalog";

const PRODUCT_NAV = [["overzicht", "Overzicht"], ["uitvoeringen", "Uitvoeringen"], ["werkwijze", "Werkwijze"], ["informatie", "Informatie"], ["kosten", "Kosten"], ["vragen", "Vragen"]];
const OVERVIEW_NAV = [["vergelijken", "Vergelijken"], ["keuzehulp", "Keuzehulp"], ["werkwijze", "Werkwijze"], ["informatie", "Informatie"], ["kosten", "Kosten"], ["vragen", "Vragen"]];

// Subnav-ankers voor catalogusblokken verschijnen alleen als er gepubliceerde items zijn.
const useCatalogNav = (page, base) => {
  const { doors } = useDoorCatalog();
  const systems = useProfileSystems();
  const material = page.doorGallery?.material;
  const nav = [...base];
  if (page.doorGallery && doors.some((d) => !material || d.material === material)) nav.splice(1, 0, ["deurstijlen", "Deurstijlen"]);
  if (page.priceKey && systems.some((s) => s.product_keys?.includes(page.priceKey))) nav.splice(nav.findIndex(([id]) => id === "werkwijze"), 0, ["profielsystemen", "Profielsystemen"]);
  return nav;
};

export default function ProductPage({ page }) {
  const crumbs = [{ label: "Home", href: "/" }, ...(page.parent ? [page.parent] : []), { label: page.label, href: page.path }];
  const faqs = useFaqs(page.path);
  const seo = usePageSeo(productTextKey(page.path));
  useSEO({
    title: seo.title,
    description: seo.description,
    path: page.path,
    ogImage: SITE_URL + MEDIA_BY_KEY[page.hero.media].src,
    jsonLd: buildServiceJsonLd(page.seo.service, seo.description),
    faqJsonLd: pageFaqJsonLd(faqs, page.path),
    breadcrumbs: buildBreadcrumbsJsonLd(crumbs),
  });
  const overview = Boolean(page.choice);
  const nav = useCatalogNav(page, overview ? OVERVIEW_NAV : PRODUCT_NAV);
  const gallery = page.doorGallery && <DoorGallery material={page.doorGallery.material} />;
  return (
    <div data-testid={`product-page-${page.path.slice(1)}`}>
      <ProductHero page={page} crumbs={crumbs} />
      <ProductSubnav items={nav} onderwerp={page.onderwerp} />
      {overview ? (
        <>
          <MaterialChoice choice={page.choice} />
          {gallery}
          <CompareTable compare={page.compare} />
          <ChoiceGuide guide={page.guide} />
        </>
      ) : (
        <>
          <ProductIntro intro={page.intro} features={page.features} />
          {gallery}
          <ProductOptions options={page.options} />
          <ProfileSystemsSection productKey={page.priceKey} onderwerp={page.onderwerp} />
        </>
      )}
      <ServiceScope onderwerp={page.onderwerp} scope={page.scope} />
      <ProductArticle title={page.articleTitle} sections={page.sections} onderwerp={page.onderwerp} />
      <ProductPrice price={page.price} priceKey={page.priceKey} onderwerp={page.onderwerp} />
      <IsdeTeaser path={page.path} />
      <ProductFAQ faqs={faqs} title={page.faqTitle} />
      <KbRelated category={kbCategoryFor(page.path)} onderwerp={page.onderwerp} tone="slate" />
      <RelatedProducts items={page.related} />
      <ProductEndCTA title={page.ctaTitle} onderwerp={page.onderwerp} preselected={!overview} />
    </div>
  );
}
